import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { Subscription } from 'rxjs';
import { JobService } from 'src/@core/services/job/job.service';

export interface OutcomeReviewCopilotData {
  entityId: string;
  projectId?: string;
  direction?: string;
  finalPrompt: string;
  outcomes: { outcome_name: string; outcome_description: string }[];
}

export interface OutcomeReviewSuggestion {
  id: string;
  action: string;
  outcome_name: string;
  proposed_name?: string | null;
  proposed_description?: string | null;
  rationale?: string;
  split_into?: { outcome_name: string; outcome_description: string }[] | null;
  decision?: 'accept' | 'reject' | 'edit' | null;
  feedback?: string;
}

export interface OutcomeReviewClarifyingQuestion {
  id: string;
  text: string;
  options: string[];
  related_outcome_name?: string | null;
  selected?: string;
  note?: string;
}

export interface OutcomeReviewProblem {
  id: string;
  outcome_name?: string | null;
  severity?: string;
  summary: string;
}

type WizardStep = 'review' | 'final';

@Component({
  selector: 'hour4u-admin-outcome-review-copilot',
  templateUrl: './outcome-review-copilot.component.html',
  styleUrls: ['./outcome-review-copilot.component.scss']
})
export class OutcomeReviewCopilotComponent implements OnInit, OnDestroy {
  @ViewChild('mainScroll', { static: false }) mainScroll: ElementRef<HTMLDivElement>;

  chatId: string = null;
  step: WizardStep = 'review';
  assistantMessage = '';
  problems: OutcomeReviewProblem[] = [];
  clarifyingQuestions: OutcomeReviewClarifyingQuestion[] = [];
  suggestions: OutcomeReviewSuggestion[] = [];
  proposedOutcomes: { outcome_name: string; outcome_description: string }[] = [];
  originalOutcomes: { outcome_name: string; outcome_description: string }[] = [];

  loading = false;
  applying = false;
  errorMessage = '';
  proceedHint = '';

  private sub: Subscription | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OutcomeReviewCopilotData,
    private dialogRef: MatDialogRef<OutcomeReviewCopilotComponent>,
    private jobService: JobService
  ) { }

  ngOnInit(): void {
    this.originalOutcomes = (this.data.outcomes || []).map(o => ({
      outcome_name: o.outcome_name,
      outcome_description: o.outcome_description || ''
    }));
    if (!this.data.finalPrompt || !String(this.data.finalPrompt).trim()) {
      this.errorMessage = 'Add the call script in the previous step first — definitions are written against it.';
      return;
    }
    if (!this.data.outcomes || !this.data.outcomes.length) {
      this.errorMessage = 'Name at least one outcome first.';
      return;
    }
    this.bootstrap();
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  get storageKey(): string {
    const projectId = this.data.projectId || '_unsaved';
    return `outcome-review-chat:${projectId}`;
  }

  get answersStorageKey(): string {
    return `${this.storageKey}:answers`;
  }

  get unansweredCount(): number {
    return this.clarifyingQuestions.filter(q => !(q.selected || '').trim()).length;
  }

  get allQuestionsAnswered(): boolean {
    if (!this.clarifyingQuestions.length) {
      return true;
    }
    return this.unansweredCount === 0;
  }

  get undecidedSuggestions(): number {
    return this.suggestions.filter(s => !s.decision).length;
  }

  get canProceed(): boolean {
    return !this.loading && !this.applying && !!this.chatId && this.allQuestionsAnswered;
  }

  get canConfirmApply(): boolean {
    return !this.loading && !this.applying && !!this.chatId
      && this.proposedOutcomes && this.proposedOutcomes.length > 0;
  }

  get changeRows(): Array<{
    outcome_name: string;
    before: string;
    after: string;
    status: 'changed' | 'added' | 'removed' | 'unchanged';
  }> {
    const beforeByName = new Map(
      this.originalOutcomes.map(o => [o.outcome_name.toLowerCase(), o])
    );
    const afterByName = new Map(
      (this.proposedOutcomes || []).map(o => [o.outcome_name.toLowerCase(), o])
    );
    const rows = [];
    (this.proposedOutcomes || []).forEach(after => {
      const before = beforeByName.get(after.outcome_name.toLowerCase());
      if (!before) {
        rows.push({
          outcome_name: after.outcome_name,
          before: '',
          after: after.outcome_description || '',
          status: 'added' as const
        });
      } else if ((before.outcome_description || '') !== (after.outcome_description || '')
        || before.outcome_name !== after.outcome_name) {
        rows.push({
          outcome_name: after.outcome_name,
          before: before.outcome_description || '',
          after: after.outcome_description || '',
          status: 'changed' as const
        });
      } else {
        rows.push({
          outcome_name: after.outcome_name,
          before: before.outcome_description || '',
          after: after.outcome_description || '',
          status: 'unchanged' as const
        });
      }
    });
    this.originalOutcomes.forEach(before => {
      if (!afterByName.has(before.outcome_name.toLowerCase())) {
        rows.push({
          outcome_name: before.outcome_name,
          before: before.outcome_description || '',
          after: '',
          status: 'removed' as const
        });
      }
    });
    return rows;
  }

  close(): void {
    this.dialogRef.close(null);
  }

  retry(): void {
    this.errorMessage = '';
    this.step = 'review';
    this.bootstrap();
  }

  selectAnswer(question: OutcomeReviewClarifyingQuestion, option: string): void {
    question.selected = option;
    this.persistLocalAnswers();
    this.proceedHint = '';
  }

  acceptSuggestion(suggestion: OutcomeReviewSuggestion): void {
    suggestion.decision = 'accept';
    this.persistLocalAnswers();
  }

  rejectSuggestion(suggestion: OutcomeReviewSuggestion): void {
    suggestion.decision = 'reject';
    this.persistLocalAnswers();
  }

  editSuggestion(suggestion: OutcomeReviewSuggestion): void {
    suggestion.decision = 'edit';
    if (!suggestion.feedback) {
      suggestion.feedback = suggestion.proposed_description || '';
    }
    this.persistLocalAnswers();
  }

  onSuggestionFeedbackChange(): void {
    this.persistLocalAnswers();
  }

  proceedToFinalReview(): void {
    if (!this.allQuestionsAnswered) {
      this.proceedHint = `Answer all clarifying questions first (${this.unansweredCount} left).`;
      return;
    }
    if (!this.chatId) {
      return;
    }

    const answers = this.clarifyingQuestions
      .filter(q => !!(q.selected || '').trim())
      .map(q => ({
        question_id: q.id,
        selected: q.selected,
        note: (q.note || '').trim() || undefined
      }));

    const suggestion_feedbacks = this.suggestions
      .filter(s => !!s.decision)
      .map(s => ({
        suggestion_id: s.id,
        decision: s.decision as 'accept' | 'reject' | 'edit',
        edited_description: s.decision === 'edit'
          ? (s.feedback || s.proposed_description || null)
          : null,
        feedback: (s.feedback || '').trim() || undefined
      }));

    // If there is nothing for the model to incorporate, jump straight to local final.
    if (!answers.length && !suggestion_feedbacks.length && this.proposedOutcomes.length) {
      this.step = 'final';
      this.scrollTop();
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    this.proceedHint = '';
    this.sub = this.jobService.continueOutcomeReviewChat(this.data.entityId, {
      chat_id: this.chatId,
      answers,
      suggestion_feedbacks
    }).subscribe(
      (response: any) => {
        this.loading = false;
        this.applyTurn(response, false);
        this.step = 'final';
        this.scrollTop();
      },
      () => {
        this.loading = false;
        this.errorMessage = 'Could not build the final review. Please try again.';
      }
    );
  }

  backToReview(): void {
    this.step = 'review';
    this.proceedHint = '';
  }

  confirmApplyToProject(): void {
    if (!this.canConfirmApply) {
      return;
    }
    this.applying = true;
    this.errorMessage = '';
    const payload: any = {
      chat_id: this.chatId,
      outcomes: this.proposedOutcomes
    };
    this.sub = this.jobService.applyOutcomeReviewChat(this.data.entityId, payload).subscribe(
      (response: any) => {
        this.applying = false;
        try {
          sessionStorage.removeItem(this.storageKey);
          sessionStorage.removeItem(this.answersStorageKey);
        } catch (e) { /* ignore */ }
        this.dialogRef.close({
          outcomes: (response && response.outcomes) ? response.outcomes : this.proposedOutcomes,
          changed_count: response && response.changed_count,
          chat_id: this.chatId,
          saveToProject: true
        });
      },
      () => {
        this.applying = false;
        this.errorMessage = 'Could not apply the reviewed outcomes. Please try again.';
      }
    );
  }

  actionLabel(action: string): string {
    switch ((action || '').toLowerCase()) {
      case 'add': return 'Add';
      case 'split': return 'Split';
      case 'rename': return 'Rename';
      case 'remove': return 'Remove';
      default: return 'Rewrite';
    }
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'added': return 'Added';
      case 'removed': return 'Removed';
      case 'changed': return 'Updated';
      default: return 'Unchanged';
    }
  }

  private bootstrap(): void {
    this.loading = true;
    this.errorMessage = '';
    // Always start a fresh batch review so the model re-scans every outcome.
    // Mid-dialog answers are still cached locally under answersStorageKey.
    try {
      sessionStorage.removeItem(this.storageKey);
    } catch (e) { /* ignore */ }

    this.sub = this.jobService.startOutcomeReviewChat(this.data.entityId, {
      outcomes: this.data.outcomes,
      final_prompt: this.data.finalPrompt,
      project_id: this.data.projectId,
      direction: this.data.direction || 'outbound'
    }).subscribe(
      (response: any) => {
        this.loading = false;
        this.applyTurn(response, true);
        this.restoreLocalAnswers();
      },
      () => {
        this.loading = false;
        this.errorMessage = 'Could not start the outcome review. Please try again.';
      }
    );
  }

  private applyTurn(response: any, resetDecisions: boolean): void {
    if (!response) {
      return;
    }
    this.chatId = response.chat_id || this.chatId;
    this.assistantMessage = response.assistant_message || this.assistantMessage;
    this.problems = response.problems || [];

    const incomingQuestions = response.clarifying_questions
      || (response.clarifying_question ? [response.clarifying_question] : []);
    this.clarifyingQuestions = (incomingQuestions || []).map((q: any) => ({
      id: q.id,
      text: q.text,
      options: q.options || [],
      related_outcome_name: q.related_outcome_name || null,
      selected: null,
      note: ''
    }));

    this.suggestions = (response.suggestions || []).map((s: any) => ({
      ...s,
      decision: resetDecisions ? null : (s.decision || null),
      feedback: resetDecisions ? '' : (s.feedback || '')
    }));
    if (response.proposed_outcomes && response.proposed_outcomes.length) {
      this.proposedOutcomes = response.proposed_outcomes;
    }

    if (this.chatId) {
      try {
        sessionStorage.setItem(this.storageKey, this.chatId);
      } catch (e) { /* ignore */ }
    }
  }

  private persistLocalAnswers(): void {
    try {
      const payload = {
        answers: this.clarifyingQuestions.map(q => ({
          id: q.id,
          selected: q.selected || null,
          note: q.note || ''
        })),
        suggestions: this.suggestions.map(s => ({
          id: s.id,
          decision: s.decision || null,
          feedback: s.feedback || ''
        }))
      };
      sessionStorage.setItem(this.answersStorageKey, JSON.stringify(payload));
    } catch (e) { /* ignore */ }
  }

  private restoreLocalAnswers(): void {
    try {
      const raw = sessionStorage.getItem(this.answersStorageKey);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      const answerById = new Map(
        ((parsed && parsed.answers) || []).map((a: any) => [a.id, a])
      );
      this.clarifyingQuestions.forEach(q => {
        const saved = answerById.get(q.id) as any;
        if (saved) {
          q.selected = saved.selected || null;
          q.note = saved.note || '';
        }
      });
      const fbById = new Map(
        ((parsed && parsed.suggestions) || []).map((s: any) => [s.id, s])
      );
      this.suggestions.forEach(s => {
        const saved = fbById.get(s.id) as any;
        if (saved) {
          s.decision = saved.decision || null;
          s.feedback = saved.feedback || '';
        }
      });
    } catch (e) { /* ignore */ }
  }

  private scrollTop(): void {
    setTimeout(() => {
      const el = this.mainScroll && this.mainScroll.nativeElement;
      if (el) {
        el.scrollTop = 0;
      }
    }, 40);
  }
}
